/**
 * Static point layer renderer — creates deck.gl layers for pre-loaded typed data arrays.
 *
 * Renders datacenters (IconLayer), spaceports, stock exchanges, financial centers,
 * central banks, commodity hubs, economic centers, and strategic waterways as
 * colored markers with data-driven sizing. Colors and params match WM visual style.
 *
 * MIT License — see THIRD-PARTY-NOTICES.md
 */

import Supercluster from 'supercluster';
import { ScatterplotLayer, IconLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import {
  AI_DATA_CENTERS,
  STOCK_EXCHANGES,
  FINANCIAL_CENTERS,
  CENTRAL_BANKS,
  COMMODITY_HUBS,
  ECONOMIC_CENTERS,
  SPACEPORTS,
  STRATEGIC_WATERWAYS,
} from '@/config/data';
import type {
  AIDataCenter,
  StockExchange,
  FinancialCenter,
  CentralBank,
  CommodityHub,
  EconomicCenter,
  Spaceport,
  StrategicWaterway,
} from '@/config/data';
import {
  OVERLAY_COLORS,
  MARKER_ICONS,
  DATACENTER_ICON_MAPPING,
} from '../layer-colors';

/** Layer IDs handled by this renderer. */
export const STATIC_POINT_LAYER_IDS = new Set([
  'datacenters',
  'stock-exchanges',
  'financial-centers',
  'central-banks',
  'commodity-hubs',
  'economic-centers',
  'spaceports',
  'waterways',
]);

// ---------------------------------------------------------------------------
// RGBA type alias
// ---------------------------------------------------------------------------

type RGBA = [number, number, number, number];

// ---------------------------------------------------------------------------
// Individual layer factories (WM parity)
// ---------------------------------------------------------------------------

/** Datacenters — IconLayer with square markers, purple. WM: createDatacentersLayer() */
function createDatacentersLayer(data: AIDataCenter[] = AI_DATA_CENTERS.filter((dc) => dc.status !== 'decommissioned')): Layer {
  return new IconLayer<AIDataCenter>({
    id: 'static-point-datacenters',
    data,
    getPosition: (d) => [d.lon, d.lat],
    getIcon: () => 'square',
    iconAtlas: MARKER_ICONS.square,
    iconMapping: DATACENTER_ICON_MAPPING,
    getSize: 10,
    getColor: (d): RGBA => {
      if (d.status === 'planned') return [136, 68, 255, 100];
      return [136, 68, 255, 140];
    },
    sizeScale: 1,
    sizeMinPixels: 6,
    sizeMaxPixels: 14,
    pickable: true,
    updateTriggers: {
      getColor: [data.length],
    },
  });
}

/** Spaceports — ScatterplotLayer, purple. WM: createSpaceportsLayer() */
function createSpaceportsLayer(): Layer {
  return new ScatterplotLayer<Spaceport>({
    id: 'static-point-spaceports',
    data: SPACEPORTS,
    getPosition: (d) => [d.lon, d.lat],
    getRadius: 10000,
    getFillColor: [200, 100, 255, 200] as RGBA,
    radiusMinPixels: 5,
    radiusMaxPixels: 12,
    pickable: true,
  });
}

/** Stock exchanges — tier-driven size/color. WM: createStockExchangesLayer() */
function createStockExchangesLayer(): Layer {
  return new ScatterplotLayer<StockExchange>({
    id: 'static-point-stock-exchanges',
    data: STOCK_EXCHANGES,
    getPosition: (d) => [d.lon, d.lat],
    getRadius: (d) => (d.tier === 'mega' ? 18000 : d.tier === 'major' ? 14000 : 11000),
    getFillColor: (d): RGBA => {
      if (d.tier === 'mega') return [255, 215, 80, 220];
      if (d.tier === 'major') return OVERLAY_COLORS.stockExchange as RGBA;
      return [140, 210, 255, 190];
    },
    radiusMinPixels: 5,
    radiusMaxPixels: 14,
    pickable: true,
    updateTriggers: {
      getFillColor: [STOCK_EXCHANGES.length],
      getRadius: [STOCK_EXCHANGES.length],
    },
  });
}

/** Financial centers — type-driven size/color. WM: createFinancialCentersLayer() */
function createFinancialCentersLayer(): Layer {
  return new ScatterplotLayer<FinancialCenter>({
    id: 'static-point-financial-centers',
    data: FINANCIAL_CENTERS,
    getPosition: (d) => [d.lon, d.lat],
    getRadius: (d) => (d.type === 'global' ? 17000 : d.type === 'regional' ? 13000 : 10000),
    getFillColor: (d): RGBA => {
      if (d.type === 'global') return OVERLAY_COLORS.financialCenter as RGBA;
      if (d.type === 'regional') return [0, 190, 130, 185];
      return [0, 150, 110, 165];
    },
    radiusMinPixels: 4,
    radiusMaxPixels: 12,
    pickable: true,
    updateTriggers: {
      getFillColor: [FINANCIAL_CENTERS.length],
      getRadius: [FINANCIAL_CENTERS.length],
    },
  });
}

/** Central banks — type-driven size/color. WM: createCentralBanksLayer() */
function createCentralBanksLayer(): Layer {
  return new ScatterplotLayer<CentralBank>({
    id: 'static-point-central-banks',
    data: CENTRAL_BANKS,
    getPosition: (d) => [d.lon, d.lat],
    getRadius: (d) => (d.type === 'major' ? 15000 : d.type === 'supranational' ? 17000 : 12000),
    getFillColor: (d): RGBA => {
      if (d.type === 'major') return OVERLAY_COLORS.centralBank as RGBA;
      if (d.type === 'supranational') return [255, 235, 140, 220];
      return [235, 180, 80, 185];
    },
    radiusMinPixels: 4,
    radiusMaxPixels: 12,
    pickable: true,
    updateTriggers: {
      getFillColor: [CENTRAL_BANKS.length],
      getRadius: [CENTRAL_BANKS.length],
    },
  });
}

/** Commodity hubs — type-driven size/color. WM: createCommodityHubsLayer() */
function createCommodityHubsLayer(): Layer {
  return new ScatterplotLayer<CommodityHub>({
    id: 'static-point-commodity-hubs',
    data: COMMODITY_HUBS,
    getPosition: (d) => [d.lon, d.lat],
    getRadius: (d) => (d.type === 'exchange' ? 14000 : d.type === 'port' ? 12000 : 10000),
    getFillColor: (d): RGBA => {
      if (d.type === 'exchange') return OVERLAY_COLORS.commodityHub as RGBA;
      if (d.type === 'port') return [80, 170, 255, 190];
      return [255, 110, 80, 185];
    },
    radiusMinPixels: 4,
    radiusMaxPixels: 11,
    pickable: true,
    updateTriggers: {
      getFillColor: [COMMODITY_HUBS.length],
      getRadius: [COMMODITY_HUBS.length],
    },
  });
}

/** Economic centers — gold dots. WM: createEconomicCentersLayer() */
function createEconomicCentersLayer(): Layer {
  return new ScatterplotLayer<EconomicCenter>({
    id: 'static-point-economic-centers',
    data: ECONOMIC_CENTERS,
    getPosition: (d) => [d.lon, d.lat],
    getRadius: 8000,
    getFillColor: [255, 215, 0, 180] as RGBA,
    radiusMinPixels: 4,
    radiusMaxPixels: 10,
    pickable: true,
  });
}

/** Strategic waterways — blue dots. WM: createWaterwaysLayer() */
function createWaterwaysLayer(): Layer {
  return new ScatterplotLayer<StrategicWaterway>({
    id: 'static-point-waterways',
    data: STRATEGIC_WATERWAYS,
    getPosition: (d) => [d.lon, d.lat],
    getRadius: 10000,
    getFillColor: [100, 150, 255, 180] as RGBA,
    radiusMinPixels: 5,
    radiusMaxPixels: 12,
    pickable: true,
  });
}

// ---------------------------------------------------------------------------
// Layer ID → factory mapping
// ---------------------------------------------------------------------------

const LAYER_FACTORIES: Record<string, () => Layer> = {
  'datacenters': createDatacentersLayer,
  'stock-exchanges': createStockExchangesLayer,
  'financial-centers': createFinancialCentersLayer,
  'central-banks': createCentralBanksLayer,
  'commodity-hubs': createCommodityHubsLayer,
  'economic-centers': createEconomicCentersLayer,
  'spaceports': createSpaceportsLayer,
  'waterways': createWaterwaysLayer,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create deck.gl layers for all active static point layers.
 * Returns one layer per active layer ID.
 *
 * The `zoom` parameter is accepted for API compatibility with the orchestrator
 * but individual layers use their own radiusMinPixels/radiusMaxPixels for sizing.
 */
export function createStaticPointLayers(
  activeLayerIds: string[],
  zoom: number,
): Layer[] {
  const layers: Layer[] = [];

  for (const layerId of activeLayerIds) {
    // Datacenters swap representation by zoom: cluster discs below 5,
    // square icons at/above (WM DeckGLMap.ts:1632, 1981-1987)
    if (layerId === 'datacenters') {
      layers.push(...createDatacenterLayersLOD(zoom));
      continue;
    }
    const factory = LAYER_FACTORIES[layerId];
    if (factory) {
      layers.push(factory());
    }
  }

  return layers;
}

// ---------------------------------------------------------------------------
// Datacenter clusters (zoom < 5) — WM DeckGLMap.ts:1597-1616, 4282-4321
// ---------------------------------------------------------------------------

interface DatacenterClusterProps {
  existingCount: number;
}

export interface DatacenterCluster {
  lng: number;
  lat: number;
  count: number;
  /** Majority of members are operational (existingCount >= max(1, count/2)). */
  majorityExisting: boolean;
}

let datacenterIndex: Supercluster<{ dc: AIDataCenter }, DatacenterClusterProps> | null = null;

function getDatacenterIndex(): Supercluster<{ dc: AIDataCenter }, DatacenterClusterProps> {
  if (datacenterIndex) return datacenterIndex;

  datacenterIndex = new Supercluster<{ dc: AIDataCenter }, DatacenterClusterProps>({
    radius: 70,
    maxZoom: 14,
    minPoints: 2,
    map: (props) => ({ existingCount: props.dc.status === 'existing' ? 1 : 0 }),
    reduce: (accumulated, props) => {
      accumulated.existingCount += props.existingCount;
    },
  });

  datacenterIndex.load(
    AI_DATA_CENTERS.filter((dc) => dc.status !== 'decommissioned').map((dc) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [dc.lon, dc.lat] },
      properties: { dc },
    })),
  );

  return datacenterIndex;
}

/** Datacenter LOD: cluster discs below zoom 5, square icons at/above. */
function createDatacenterLayersLOD(zoom: number): Layer[] {
  if (zoom >= 5) {
    return [createDatacentersLayer()];
  }

  const features = getDatacenterIndex().getClusters([-180, -85, 180, 85], Math.floor(zoom));
  const clusters: DatacenterCluster[] = [];
  const singles: AIDataCenter[] = [];

  for (const f of features) {
    const props = f.properties;
    if ('cluster' in props && props.cluster) {
      const count = (props as unknown as { point_count: number }).point_count;
      const existingCount = (props as unknown as DatacenterClusterProps).existingCount;
      clusters.push({
        lng: f.geometry.coordinates[0],
        lat: f.geometry.coordinates[1],
        count,
        majorityExisting: existingCount >= Math.max(1, count / 2),
      });
    } else {
      singles.push((props as { dc: AIDataCenter }).dc);
    }
  }

  const layers: Layer[] = [];
  if (singles.length > 0) {
    layers.push(createDatacentersLayer(singles));
  }

  if (clusters.length > 0) {
    layers.push(new ScatterplotLayer<DatacenterCluster>({
      id: 'datacenter-clusters-layer',
      data: clusters,
      pickable: true,
      filled: true,
      radiusUnits: 'meters' as const,
      getPosition: (d) => [d.lng, d.lat],
      getRadius: (d) => 15_000 + d.count * 2000,
      // Purple = operational majority, blue = planned majority
      getFillColor: (d) => d.majorityExisting
        ? [160, 80, 255, 180] as [number, number, number, number]
        : [80, 160, 255, 180] as [number, number, number, number],
      radiusMinPixels: 6,
      radiusMaxPixels: 20,
    }));

    layers.push(new TextLayer<DatacenterCluster>({
      id: 'datacenter-clusters-badge',
      data: clusters.filter((c) => c.count > 1),
      pickable: false,
      getPosition: (d) => [d.lng, d.lat],
      getText: (d) => String(d.count),
      getSize: 12,
      getColor: [255, 255, 255, 255],
      getTextAnchor: 'middle' as const,
      getAlignmentBaseline: 'center' as const,
      fontFamily: 'system-ui, sans-serif',
      fontWeight: 700,
      sizeUnits: 'pixels' as const,
      background: true,
      getBackgroundColor: [0, 0, 0, 180],
      backgroundPadding: [4, 2, 4, 2],
      getPixelOffset: [0, -14],
    }));
  }

  return layers;
}
